import { useMemo } from 'react';
import type { MappingResponse } from '../../services/api';

export interface UseGuardrailStatusOptions {
  mappingResponse?: MappingResponse;
  selectedProjectId?: string | null;
  schemaId?: string | null;
}

export interface GuardrailStatus {
  issues: string[];
  message: string | null;
  blocked: boolean;
  label: string | null;
  graphProjectId: string | null;
  graphSchemaId: string | null;
}

export function useGuardrailStatus({
  mappingResponse,
  selectedProjectId,
  schemaId,
}: UseGuardrailStatusOptions): GuardrailStatus {
  return useMemo(() => {
    const issues: string[] = [];
    const graphProjectId = mappingResponse?.project_id ?? null;
    const graphSchemaId = mappingResponse?.target_schema_id ?? null;
    const activeProjectId = selectedProjectId ?? mappingResponse?.project_id ?? null;
    const activeSchemaId = schemaId ?? mappingResponse?.target_schema_id ?? null;

    if (graphProjectId) {
      if (!activeProjectId) {
        issues.push(
          `Canvas is pinned to project ${graphProjectId}. Select that project to make changes.`,
        );
      } else if (graphProjectId !== activeProjectId) {
        issues.push(
          `Canvas is pinned to project ${graphProjectId}, but active project is ${activeProjectId}.`,
        );
      }
    }

    if (graphSchemaId) {
      if (!activeSchemaId) {
        issues.push(
          `Canvas requires schema set ${graphSchemaId}. Assign it to the active project before editing.`,
        );
      } else if (graphSchemaId !== activeSchemaId) {
        issues.push(
          `Canvas uses schema set ${graphSchemaId}, but active schema set is ${activeSchemaId}.`,
        );
      }
    }

    const labelTokens: string[] = [];
    if (
      graphProjectId &&
      ((activeProjectId && graphProjectId !== activeProjectId) || !activeProjectId)
    ) {
      labelTokens.push('Project');
    }
    if (
      graphSchemaId &&
      ((activeSchemaId && graphSchemaId !== activeSchemaId) || !activeSchemaId)
    ) {
      labelTokens.push('Schema');
    }

    return {
      issues,
      message: issues.length ? issues.join(' ') : null,
      blocked: issues.length > 0,
      label: labelTokens.length ? `${labelTokens.join(' & ')} guardrail` : null,
      graphProjectId,
      graphSchemaId,
    };
  }, [
    mappingResponse?.project_id,
    mappingResponse?.target_schema_id,
    selectedProjectId,
    schemaId,
  ]);
}
